import type { AnimatedComponent, UseTransitionProps } from 'react-spring'
import { animated, useTransition } from 'react-spring'
import { __DEV__ } from '@mote/helpers/constants'
import { mergeClassnames } from '@mote/helpers/dom'
import type { SystemProps } from '@xstyled/styled-components'
import { x } from '@xstyled/styled-components'
import type { HTMLXStyledProps, XComponent } from '../../system'
import { forwardRef } from '../../system'
import { useDialogContext } from './DialogProvider'

export interface DialogOverlayProps extends HTMLXStyledProps<'div'> {
  showOverlay?: boolean
}

const Motion = animated(x.div) as XComponent<'div'> & AnimatedComponent<'div'>

export const DialogOverlay = forwardRef<DialogOverlayProps, 'div'>(
  (props, ref) => {
    const { className, showOverlay = true, ...rest } = props
    const _className = mergeClassnames('mote-dialog__overlay', className)

    const { motionPreset, isOpen, useAbsolutePosition } = useDialogContext()
    const motionProps = (
      motionPreset === 'none'
        ? {}
        : {
            from: { opacity: 0 },
            enter: { opacity: 1 },
            leave: { opacity: 0 },
            config: {
              duration: 150
            }
          }
    ) as UseTransitionProps
    const transition = useTransition(isOpen, motionProps)
    const positionProps: SystemProps = useAbsolutePosition
      ? { position: 'absolute', h: '100%', w: '100%' }
      : {
          position: 'fixed',
          h: '100vh',
          w: '100vw'
        }
    const styleProps: SystemProps = {
      ...rest,
      ...positionProps,
      bg: rest.bg ?? 'blackAlpha.600'
    }

    if (!showOverlay) {
      styleProps.bg = 'transparent'
    }

    return transition((style, item) =>
      item ? (
        <Motion
          ref={ref}
          className={_className}
          {...styleProps}
          tabIndex={-1}
          left={0}
          top={0}
          zIndex={10000}
          style={{ ...style, ...rest.style }}
        />
      ) : (
        <></>
      )
    )
  }
)

if (__DEV__) {
  DialogOverlay.displayName = 'DialogOverlay'
}
